/**
 * `/campaigns` presentation logic (email-campaigns, 2026-10-17): the rates
 * worked out from Resend's counts, the pooled figures behind the stat tiles,
 * what each checkpoint cell says, the chart series and the learnings split.
 * Pure: the page renders what these return, and `campaigns.test.ts` pins
 * them.
 *
 * Every rate has one stated denominator: a share of DELIVERED, except
 * bounces, a share of SENT. Resend's own `*_rate` fields are not read, so no
 * figure on the page depends on a denominator Resend chose.
 *
 * The wire shapes mirror the `list_email_campaigns` / `get_email_campaign`
 * outputs (`packages/canonical-actions/src/actions/email-campaigns-shared.ts`).
 * Since migration 233 a Clavis-sent edition goes out as one row per
 * lifecycle and arm: the tiles count it once, and the table keeps its rows
 * together (`orderCampaignRows`); `newsletter-editions.ts` names them.
 */

import {
  CLAVIS_UNSUBSCRIBE_LINK_URL,
  EMAIL_CAMPAIGN_CHECKPOINTS,
  EMAIL_CAMPAIGN_MAX_CHECKPOINT_ATTEMPTS,
  EMAIL_CAMPAIGN_UNSUBSCRIBE_LINK_URL,
  type EmailCampaignCheckpointKey,
  type EmailCampaignLearningConfidence,
  type EmailCampaignLearningKind,
} from "@layer/canonical-data/email-campaigns";
import { MARKETING_LIFECYCLES } from "@layer/canonical-data/marketing-audience";

// ---------------------------------------------------------------------------
// Wire shapes.
// ---------------------------------------------------------------------------

export type CampaignMetrics = Readonly<Record<string, number>>;

export interface CampaignCheckpoint {
  readonly checkpoint: EmailCampaignCheckpointKey;
  readonly window_start: string;
  readonly as_of: string;
  readonly captured_at: string | null;
  readonly metrics: CampaignMetrics | null;
  readonly attempts: number;
  readonly last_error_code: string | null;
}

export interface CampaignSummary {
  readonly campaign_id: string;
  readonly kind: "resend_broadcast" | "clavis_send";
  /**
   * Migration 233 — a Clavis-sent newsletter's row belongs to an edition and
   * carries one lifecycle and one arm of it; all five are null on a Resend
   * broadcast.
   */
  readonly edition_id: string | null;
  readonly edition_slug: string | null;
  readonly edition_title: string | null;
  readonly audience_key: string | null;
  readonly variant_key: string | null;
  readonly resend_broadcast_id: string | null;
  readonly name: string;
  readonly subject: string | null;
  readonly preview_text: string | null;
  readonly from_address: string | null;
  readonly vendor_status: string;
  readonly is_test: boolean;
  readonly recipient_count: number | null;
  readonly scheduled_at: string | null;
  readonly sent_at: string | null;
  readonly latest_metrics: CampaignMetrics | null;
  readonly latest_metrics_at: string | null;
  readonly checkpoints: ReadonlyArray<CampaignCheckpoint>;
  readonly active_learnings: number;
}

export interface CampaignLearning {
  readonly learning_id: string;
  readonly campaign_id: string | null;
  readonly campaign_name: string | null;
  readonly audience_key: string | null;
  readonly kind: EmailCampaignLearningKind;
  readonly statement: string;
  readonly evidence: Readonly<Record<string, unknown>>;
  readonly confidence: EmailCampaignLearningConfidence | null;
  readonly status: "active" | "superseded";
  readonly superseded_by: string | null;
  readonly created_by: string;
  readonly created_at: string;
}

export interface CampaignsList {
  readonly campaigns: ReadonlyArray<CampaignSummary>;
  readonly learnings: ReadonlyArray<CampaignLearning>;
  readonly sync: {
    readonly broadcasts_synced_at: string | null;
    readonly contacts_synced_at: string | null;
    readonly last_error_code: string | null;
  } | null;
  readonly unsubscribe_imports: {
    readonly opted_out: number;
    readonly already_opted_out: number;
    readonly no_member: number;
    readonly signed_up_after: number;
    readonly last_processed_at: string | null;
  };
}

export interface CampaignClickedLink {
  readonly url: string;
  readonly clicks: number;
  readonly unique_clicks: number;
}

export interface CampaignDetail {
  readonly campaign: CampaignSummary & {
    readonly html: string | null;
    readonly html_sha256: string | null;
    readonly vendor_created_at: string | null;
    readonly clicked_links: ReadonlyArray<CampaignClickedLink>;
    readonly clicked_links_at: string | null;
    readonly metadata: Readonly<Record<string, unknown>>;
  };
  readonly learnings: ReadonlyArray<CampaignLearning>;
}

// ---------------------------------------------------------------------------
// Rates.
// ---------------------------------------------------------------------------

export interface CampaignRates {
  readonly sent: number | null;
  readonly delivered: number | null;
  /** unique opens ÷ delivered (inflated by Apple Mail Privacy Protection). */
  readonly openRate: number | null;
  /** unique clicks ÷ delivered. */
  readonly clickRate: number | null;
  /** unsubscribes ÷ delivered. */
  readonly unsubscribeRate: number | null;
  /** bounces ÷ sent. */
  readonly bounceRate: number | null;
  /** spam complaints ÷ delivered. */
  readonly complaintRate: number | null;
}

export function metricCount(m: CampaignMetrics | null, key: string): number | null {
  if (m === null) return null;
  const v = m[key];
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

function share(part: number | null, whole: number | null): number | null {
  if (part === null || whole === null || whole <= 0) return null;
  return part / whole;
}

export function campaignRates(m: CampaignMetrics | null): CampaignRates {
  const sent = metricCount(m, "sent");
  const delivered = metricCount(m, "delivered");
  return {
    sent,
    delivered,
    openRate: share(metricCount(m, "unique_opened"), delivered),
    clickRate: share(metricCount(m, "unique_clicked"), delivered),
    unsubscribeRate: share(metricCount(m, "unsubscribed"), delivered),
    bounceRate: share(metricCount(m, "bounced"), sent),
    complaintRate: share(metricCount(m, "complained"), delivered),
  };
}

const POOLED_KEYS = [
  "sent",
  "delivered",
  "unique_opened",
  "unique_clicked",
  "unsubscribed",
  "bounced",
  "complained",
] as const;

export interface PooledRates extends CampaignRates {
  /**
   * Newsletters counted — real (not test) sends inside the window with
   * totals. An edition counts once however many lifecycle and arm rows it
   * went out as.
   */
  readonly sends: number;
}

/**
 * The stat tiles: every real send of the last `days` days pooled — counts
 * summed, then divided, so a big send weighs what it reached. An edition's
 * rows each reached different people, so all of them are summed.
 */
export function pooledRates(
  campaigns: ReadonlyArray<CampaignSummary>,
  now: Date,
  days: number,
): PooledRates {
  const since = now.getTime() - days * 86_400_000;
  const sum: Record<string, number> = {};
  const sends = new Set<string>();
  for (const c of campaigns) {
    if (c.is_test || c.sent_at === null || c.latest_metrics === null) continue;
    const at = Date.parse(c.sent_at);
    if (!Number.isFinite(at) || at < since || at > now.getTime()) continue;
    sends.add(c.edition_id ?? c.campaign_id);
    for (const k of POOLED_KEYS) sum[k] = (sum[k] ?? 0) + (metricCount(c.latest_metrics, k) ?? 0);
  }
  return { sends: sends.size, ...campaignRates(sends.size === 0 ? null : sum) };
}

/**
 * The newsletters table's order: the server's (newest first), except that an
 * edition's rows sit together where its first row falls, lifecycle by
 * lifecycle (members, trial, win-back, leads), arm A before arm B.
 */
export function orderCampaignRows(
  campaigns: ReadonlyArray<CampaignSummary>,
): ReadonlyArray<CampaignSummary> {
  const groups = new Map<string, CampaignSummary[]>();
  for (const c of campaigns) {
    const key = c.edition_id === null ? `c:${c.campaign_id}` : `e:${c.edition_id}`;
    const group = groups.get(key);
    if (group === undefined) groups.set(key, [c]);
    else group.push(c);
  }
  const lifecycles: ReadonlyArray<string> = MARKETING_LIFECYCLES;
  const rank = (c: CampaignSummary): number => {
    const l = lifecycles.indexOf(c.audience_key ?? "");
    return (l === -1 ? lifecycles.length : l) * 2 + (c.variant_key === "B" ? 1 : 0);
  };
  return [...groups.values()].flatMap((g) => (g.length > 1 ? [...g].sort((a, b) => rank(a) - rank(b)) : g));
}

/** `6.1%`; `<0.1%` for a rate above zero that would round to nothing; `—` for none. */
export function fmtPct(rate: number | null, digits = 1): string {
  if (rate === null) return "—";
  const pct = rate * 100;
  const floor = 10 ** -digits;
  if (pct > 0 && pct < floor) return `<${floor.toFixed(digits)}%`;
  return `${pct.toFixed(digits)}%`;
}

// ---------------------------------------------------------------------------
// The checkpoint grid.
// ---------------------------------------------------------------------------

/** What the newsletters table shows in each checkpoint column. */
export const GRID_METRICS = [
  { key: "clicks", label: "Unique clicks", count: "unique_clicked", rate: (r: CampaignRates) => r.clickRate },
  { key: "unsubscribes", label: "Unsubscribes", count: "unsubscribed", rate: (r: CampaignRates) => r.unsubscribeRate },
  { key: "opens", label: "Unique opens", count: "unique_opened", rate: (r: CampaignRates) => r.openRate },
] as const;
export type GridMetric = (typeof GRID_METRICS)[number];
export type GridMetricKey = GridMetric["key"];

export function gridMetric(key: string): GridMetric {
  return GRID_METRICS.find((m) => m.key === key) ?? GRID_METRICS[0];
}

export type CheckpointCell =
  /** No checkpoint row: the send has not gone out. */
  | { readonly state: "none" }
  | {
      readonly state: "captured";
      readonly rate: number | null;
      readonly count: number | null;
      readonly asOf: string;
    }
  /** Its hour has not come yet. */
  | { readonly state: "upcoming"; readonly asOf: string }
  /** Its hour has passed; the tracker takes it on its next hourly run. */
  | { readonly state: "capturing"; readonly asOf: string }
  | {
      readonly state: "retrying";
      readonly asOf: string;
      readonly attempts: number;
      readonly errorCode: string | null;
    }
  /** Failed every attempt the tracker makes; it has stopped trying. */
  | { readonly state: "failed"; readonly attempts: number; readonly errorCode: string | null };

export function checkpointCell(
  campaign: CampaignSummary,
  key: EmailCampaignCheckpointKey,
  metric: GridMetric,
  now: Date,
): CheckpointCell {
  const k = campaign.checkpoints.find((c) => c.checkpoint === key);
  if (k === undefined) return { state: "none" };
  if (k.captured_at !== null && k.metrics !== null) {
    return {
      state: "captured",
      rate: metric.rate(campaignRates(k.metrics)),
      count: metricCount(k.metrics, metric.count),
      asOf: k.as_of,
    };
  }
  if (k.attempts >= EMAIL_CAMPAIGN_MAX_CHECKPOINT_ATTEMPTS) {
    return { state: "failed", attempts: k.attempts, errorCode: k.last_error_code };
  }
  if (k.attempts > 0) {
    return { state: "retrying", asOf: k.as_of, attempts: k.attempts, errorCode: k.last_error_code };
  }
  return Date.parse(k.as_of) > now.getTime()
    ? { state: "upcoming", asOf: k.as_of }
    : { state: "capturing", asOf: k.as_of };
}

export interface CheckpointPoint {
  readonly checkpoint: EmailCampaignCheckpointKey;
  /** Percentages, two decimals — what the chart plots. */
  readonly opens: number | null;
  readonly clicks: number | null;
  readonly unsubscribes: number | null;
}

/** The captured checkpoints in order, for the detail chart. */
export function checkpointSeries(campaign: CampaignSummary): ReadonlyArray<CheckpointPoint> {
  const pct = (r: number | null): number | null => (r === null ? null : Math.round(r * 10_000) / 100);
  const out: CheckpointPoint[] = [];
  for (const { key } of EMAIL_CAMPAIGN_CHECKPOINTS) {
    const k = campaign.checkpoints.find((c) => c.checkpoint === key);
    if (k === undefined || k.captured_at === null || k.metrics === null) continue;
    const r = campaignRates(k.metrics);
    out.push({
      checkpoint: key,
      opens: pct(r.openRate),
      clicks: pct(r.clickRate),
      unsubscribes: pct(r.unsubscribeRate),
    });
  }
  return out;
}

// ---------------------------------------------------------------------------
// Learnings and the sync line.
// ---------------------------------------------------------------------------

/**
 * How a clicked-link row reads. The merged row of the per-reader unsubscribe
 * links (the tracker's merge: Resend's, and Clavis's own on the newsletters
 * Clavis sends) is words, not a link; any other URL is a link only when it
 * is http(s).
 */
export function clickedLinkView(url: string): { readonly text: string; readonly href: string | null } {
  if (url === EMAIL_CAMPAIGN_UNSUBSCRIBE_LINK_URL || url === CLAVIS_UNSUBSCRIBE_LINK_URL) {
    return { text: "Unsubscribe link (every reader gets their own; added up here)", href: null };
  }
  return { text: url, href: /^https?:\/\//i.test(url) ? url : null };
}

export const LEARNING_KIND_LABEL: Readonly<Record<EmailCampaignLearningKind, string>> = {
  observation: "Observation",
  conclusion: "Conclusion",
  next_test: "Next test",
};

export function splitLearnings(learnings: ReadonlyArray<CampaignLearning>): {
  readonly active: ReadonlyArray<CampaignLearning>;
  readonly superseded: ReadonlyArray<CampaignLearning>;
} {
  return {
    active: learnings.filter((l) => l.status === "active"),
    superseded: learnings.filter((l) => l.status === "superseded"),
  };
}

/** `just now`, `12 min ago`, `3 h ago`, `2 days ago`. */
export function agoText(iso: string, now: Date): string {
  const ms = now.getTime() - Date.parse(iso);
  if (!Number.isFinite(ms)) return "—";
  const min = Math.floor(ms / 60_000);
  if (min < 1) return "just now";
  if (min < 60) return `${min} min ago`;
  const h = Math.floor(min / 60);
  if (h < 48) return `${h} h ago`;
  return `${Math.floor(h / 24)} days ago`;
}

/** The line under the title: when the tracker last read Resend, and its last error. */
export function syncLine(sync: CampaignsList["sync"], now: Date): string {
  // The tracker's code for a sending-only Resend key (the connector's
  // `RESEND_API_KEY_RESTRICTED_CODE`): nothing can be read until the key is
  // replaced, so say what to do rather than show the code.
  if (sync !== null && sync.last_error_code === "resend_api_key_restricted") {
    return "Clavis cannot read this studio's newsletters: its Resend API key can only send email. A full-access key is needed to track them.";
  }
  if (sync === null || sync.broadcasts_synced_at === null) {
    return "Not synced with Resend yet: the tracker runs every hour at :23.";
  }
  const base = `Synced with Resend ${agoText(sync.broadcasts_synced_at, now)}.`;
  return sync.last_error_code === null ? base : `${base} Last error: ${sync.last_error_code}.`;
}

/** What the `/campaigns` tablist reads of a key press. */
export interface TabKeyPress {
  readonly key: string;
  readonly altKey: boolean;
  readonly ctrlKey: boolean;
  readonly metaKey: boolean;
  readonly shiftKey: boolean;
}

/**
 * The tab a key press on the `/campaigns` tablist opens, as an index into its
 * `count` tabs; `null` when the key is not the tablist's. The arrow keys move
 * one tab, round the ends, and Home and End jump to them. With Alt, Ctrl, Cmd
 * or Shift held a key is the browser's or the system's — Alt+Left and
 * Cmd+Left go back — so it is left alone (2026-10-19 review).
 */
export function tabKeyTarget(press: TabKeyPress, current: number, count: number): number | null {
  if (press.altKey || press.ctrlKey || press.metaKey || press.shiftKey || count <= 0) return null;
  switch (press.key) {
    case "ArrowRight":
      return (current + 1) % count;
    case "ArrowLeft":
      return (current - 1 + count) % count;
    case "Home":
      return 0;
    case "End":
      return count - 1;
    default:
      return null;
  }
}
